"use client";

import { motion } from "framer-motion";
import BrowserWindow from "@/components/landing/Hero/BrowserWindow";
import FloatingCards from "@/components/landing/Hero/FloatingCards";
import TwinkleStarSvg from "@/components/landing/Hero/svg/TwinkleStarSvg";
import { useHeroMotion } from "@/components/landing/Hero/useHeroMotion";

interface HeroStorePreviewProps {
	image?: string;
	imageAlt: string;
	locale: string;
}

function HeroStorePreview(prop: HeroStorePreviewProps) {
	const { image, imageAlt, locale } = prop;
	const {
		stageRef,
		mx,
		my,
		glowX,
		glowY,
		shadowX,
		browserX,
		browserY,
		browserRotateX,
		browserRotateY,
		handleMouseMove,
		handleMouseLeave,
	} = useHeroMotion();

	return (
		<div
			ref={stageRef}
			onMouseMove={handleMouseMove}
			onMouseLeave={handleMouseLeave}
			className="relative"
			style={{ perspective: "1600px" }}
		>
			{/* Organic ambient glow — wraps the browser instead of sitting behind
			    it as a centered circle; heavy blur reads as light bloom, not a shape.
			    The cursor parallax (motion x/y) lives on the wrapper; the slow
			    organic morph (CSS keyframes) lives on the inner div, since both
			    would otherwise fight over the `transform` property. */}
			<motion.div
				className="pointer-events-none absolute -inset-x-10 -top-16 -bottom-10 -z-10"
				style={{ x: glowX, y: glowY }}
			>
				<div
					className="jana-organic-glow h-full w-full opacity-60 blur-[70px]"
					style={{
						background:
							"linear-gradient(135deg, color-mix(in srgb, var(--brand-2) 55%, transparent), color-mix(in srgb, var(--brand-1) 40%, transparent) 55%, transparent 80%)",
					}}
				/>
			</motion.div>
			<motion.div
				className="pointer-events-none absolute -end-8 bottom-0 -z-10 h-2/3 w-2/3"
				style={{ x: glowX, y: glowY }}
			>
				<div
					className="jana-organic-glow h-full w-full opacity-40 blur-[60px]"
					style={{
						animationDelay: "-9s",
						background:
							"radial-gradient(ellipse at 60% 60%, color-mix(in srgb, var(--brand-2) 45%, transparent), transparent 70%)",
					}}
				/>
			</motion.div>

			{/* Soft cast shadow on the page, echoing the browser's own drift. */}
			<motion.div
				className="pointer-events-none absolute inset-x-10 -bottom-6 -z-10 h-16 rounded-[100%] bg-black/15 blur-2xl"
				style={{ x: shadowX }}
			/>

			<TwinkleStarSvg className="absolute end-6 -top-6 z-20 h-6 w-6" />
			<TwinkleStarSvg
				className="absolute -start-4 bottom-24 z-20 hidden h-4 w-4 xl:block"
				style={{ animationDelay: "-1.8s" }}
			/>
			<span className="jana-twinkle absolute -start-8 top-1/2 z-0 hidden h-2 w-2 rounded-full bg-[var(--brand-2)]/70 blur-[1px] xl:block" />
			<span
				className="jana-twinkle absolute end-16 top-4 z-0 hidden h-1.5 w-1.5 rounded-full bg-[var(--brand-1)]/60 blur-[1px] xl:block"
				style={{ animationDelay: "-2.4s" }}
			/>

			<FloatingCards locale={locale} showExtras={!image} mx={mx} my={my} />

			<BrowserWindow
				image={image}
				imageAlt={imageAlt}
				locale={locale}
				browserX={browserX}
				browserY={browserY}
				browserRotateX={browserRotateX}
				browserRotateY={browserRotateY}
			/>
		</div>
	);
}

export default HeroStorePreview;
